import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, Loader2, Send, Sparkles } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Select } from '../../../components/ui';
import MarkdownContent from '../../../components/MarkdownContent';
import { useNotification } from '../../../contexts/NotificationContext';
import AiAssistantProgressPanel from '../../emails/components/AiAssistantProgressPanel';
import type { AiChatMessage } from '../types';
import type { AiModelOption } from '../../configuration-settings';

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

interface ChatMessageBubbleProps {
  msg: AiChatMessage;
}

const ChatMessageBubble: React.FC<ChatMessageBubbleProps> = ({ msg }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [copied, setCopied] = useState(false);
  const isUser = msg.role === 'user';

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(msg.content);
    if (ok) {
      setCopied(true);
      showSuccess('Copied to clipboard');
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      showError('Copy not available');
    }
  }, [msg.content, showError, showSuccess]);

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'group relative max-w-[85%] rounded-2xl px-4 py-3 pr-10 text-sm shadow-sm',
          isUser
            ? 'bg-indigo-600 text-white rounded-br-md'
            : cn(colors.surface, colors.border, 'border rounded-bl-md'),
        )}
      >
        {isUser ? (
          <MarkdownContent
            content={msg.content}
            className="prose-invert [&_*]:text-white [&_strong]:text-white [&_hr]:border-white/30"
          />
        ) : (
          <MarkdownContent content={msg.content} className="text-sm" />
        )}
        <button
          type="button"
          onClick={() => void handleCopy()}
          className={cn(
            'absolute top-1.5 right-1.5 rounded-lg p-1.5 transition-all',
            'opacity-70 hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100',
            isUser
              ? 'text-white hover:bg-white/20'
              : cn(colors.textSecondary, 'hover:bg-black/[0.06] dark:hover:bg-white/[0.08]'),
          )}
          title="Copy message"
          aria-label="Copy message"
        >
          {copied ? (
            <Check className="h-4 w-4" strokeWidth={2} aria-hidden />
          ) : (
            <Copy className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
};

interface ChatPanelProps {
  conversationId: number | null;
  conversationTitle?: string;
  messages: AiChatMessage[];
  models: AiModelOption[];
  selectedModelId: string;
  onModelChange: (modelId: string) => void;
  loadingMessages?: boolean;
  sending?: boolean;
  onSend: (content: string) => Promise<boolean>;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  conversationId,
  conversationTitle,
  messages,
  models,
  selectedModelId,
  onModelChange,
  loadingMessages,
  sending,
  onSend,
}) => {
  const { colors } = useTheme();
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  useEffect(() => {
    setInput('');
  }, [conversationId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !conversationId || sending) return;
    setInput('');
    const ok = await onSend(text);
    if (!ok) setInput(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit(e);
    }
  };

  if (!conversationId) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center p-8 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25">
          <Sparkles className="h-7 w-7" aria-hidden />
        </div>
        <h2 className={cn('text-lg font-semibold', colors.text)}>AI Assistant Chat</h2>
        <p className={cn('mt-2 max-w-md text-sm', colors.textMuted)}>
          Select a conversation from the history or start a new chat to ask questions about your work,
          draft content, or get help with CRM tasks.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className={cn('flex-shrink-0 border-b px-4 py-3', colors.border)}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className={cn('truncate text-base font-semibold', colors.text)}>
              {conversationTitle || 'Chat'}
            </h2>
            <p className={cn('text-xs', colors.textMuted)}>Multi-turn AI conversation</p>
          </div>
          {models.length > 0 ? (
            <div className="w-full max-w-[220px]">
              <Select
                name="ai-chat-model"
                value={selectedModelId}
                onChange={(e) => onModelChange(String(e.target.value))}
                options={models.map((m) => ({ value: m.id, label: m.label }))}
                showClearButton={false}
                searchPlaceholder="Search models…"
                className="w-full"
                disabled={sending}
              />
            </div>
          ) : null}
        </div>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {loadingMessages ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className={cn('h-6 w-6 animate-spin', colors.textMuted)} aria-hidden />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Sparkles className={cn('h-8 w-8 mb-3', colors.textMuted)} aria-hidden />
            <p className={cn('text-sm', colors.textMuted)}>Send a message to start this conversation.</p>
          </div>
        ) : (
          <div className="mx-auto flex max-w-3xl flex-col gap-4">
            {messages.map((msg) => (
              <ChatMessageBubble key={msg.id} msg={msg} />
            ))}
          </div>
        )}

        {sending ? (
          <div className="mx-auto mt-4 max-w-3xl">
            <AiAssistantProgressPanel promptName="Generating reply…" compact />
          </div>
        ) : null}
      </div>

      <form
        onSubmit={handleSubmit}
        className={cn('flex-shrink-0 border-t p-4', colors.border, 'bg-black/[0.02] dark:bg-white/[0.02]')}
      >
        <div className="mx-auto flex max-w-3xl items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            placeholder="Ask the AI assistant… (Enter to send, Shift+Enter for new line)"
            rows={2}
            className={cn(
              'min-h-[44px] max-h-40 flex-1 resize-y rounded-xl border px-3 py-2.5 text-sm',
              colors.text,
              colors.border,
              'bg-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
            )}
          />
          <Button
            type="submit"
            variant="default"
            disabled={!input.trim() || sending}
            className="h-11 w-11 shrink-0 rounded-xl p-0"
            aria-label="Send message"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChatPanel;
